<script setup>
import { useEventCustomerMembershipQuery } from '@/schedule/composables/useEventCustomerMembershipQuery'

const props = defineProps({
  eligibility: {
    type: Object,
  },
  event: {
    type: Object,
  },
})

const emit = defineEmits(['update'])

const { t } = useI18n()

const isAppointment = toRef(() => props.event?.type === 'appointment')

const {
  data: eventCustomerMemberships,
  isPending: eventCustomerMembershipsLoading,
} = useEventCustomerMembershipQuery(isAppointment.value ? props.event?.offering_type : props.event?.id, isAppointment.value)

const isReservationAllowed = toRef(() => props.eligibility.recurring_reservations?.allowed && eventCustomerMemberships.value?.length > 0)
const showPurchaseOptions = ref(false)

const availableMemberships = computed(() => {
  return [
    ...(props.eligibility?.drop_in_price
      ? [{
          name: t('Single Session'),
          description: t('Pay for this single registration.'),
          price: props.eligibility.drop_in_price,
          display_price: props.eligibility.drop_in_price,
        }]
      : []),
    ...(eventCustomerMemberships.value ? eventCustomerMemberships.value : []),
  ]
})

function viewRegistrationOptions() {
  showPurchaseOptions.value = !showPurchaseOptions.value
}
</script>

<template>
  <DSLoadingWrapper :loading="eventCustomerMembershipsLoading">
    <div class="flex grow flex-col justify-between flex-gap-y-4 whitespace-pre-wrap">
      <template v-if="!showPurchaseOptions">
        <DSAlert
          type="warning"
          no-close
          class="w-100%"
        >
          {{ t('Purchase Required') }}
        </DSAlert>
        <div class="flex flex-col flex-gap-y-2">
          <DSButton
            class="btn btn-md w-full truncate uppercase btn-fill-primary"
            @click="viewRegistrationOptions()"
          >
            {{ t('View Registration Options') }}
          </DSButton>
          <BookingModalNewReservationButton
            v-if="isReservationAllowed"
            :eligibility="eligibility"
            :event="event"
          />
        </div>
      </template>
      <template v-else>
        <BookingModalPurchaseOptionsCard
          :event-id="event?.id"
          :available-memberships="availableMemberships"
          :is-appointment="isAppointment"
          @back="viewRegistrationOptions"
          @update="emit('update')"
        />
      </template>
    </div>
  </DSLoadingWrapper>
</template>

<i18n>
{
  "en-us": {
    "Single Session": "Single Session",
    "Pay for this single registration.": "Pay for this single registration.",
    "Purchase Required": "Purchase Required",
    "View Registration Options": "View Registration Options"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Pay for this single registration.": "Für diese Einzelregistrierung bezahlen",
  "Purchase Required": "Kauf erforderlich",
  "View Registration Options": "Registrierungsoptionen anzeigen",
  "Single Session": "Einzelsitzung"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Pay for this single registration.": "Payer pour cette inscription simple.",
  "Purchase Required": "Achat requis",
  "View Registration Options": "Afficher les options d'inscription",
  "Single Session": "Séance unique"
}
</i18n>

<i18n locale="es" lang="json">
{
  "Pay for this single registration.": "Pague por esta inscripción única.",
  "Purchase Required": "Compra obligatoria",
  "View Registration Options": "Ver opciones de inscripción",
  "Single Session": "Sesión única"
}
</i18n>
